import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, TOKEN_REFRESH_THRESHOLD } from '@/lib/constants';
import { useAuthStore } from '@/application/stores/auth.store';

export interface ApiError {
  statusCode: number;
  message: string;
  error: string;
  code?: string;
  details?: Record<string, string[]>;
}

const httpClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: attach access token
httpClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken } = useAuthStore.getState();
    if (accessToken !== null) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// Response interceptor: handle 401 (token refresh)
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function subscribeTokenRefresh(cb: (token: string) => void): void {
  refreshSubscribers.push(cb);
}

function onTokenRefreshed(token: string): void {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean } | undefined;

    if (
      error.response?.status === 401 &&
      originalRequest !== undefined &&
      originalRequest._retry !== true &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/login')
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((token: string) => {
            if (token === '') {
              reject(error);
              return;
            }
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(httpClient(originalRequest));
          });
        });
      }

      isRefreshing = true;
      originalRequest._retry = true;
      const { refreshToken, setTokens, logout } = useAuthStore.getState();

      if (refreshToken === null) {
        logout();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await httpClient.post<{
          accessToken: string;
          refreshToken: string;
          accessTokenExpiresAt: number;
        }>('/auth/refresh', { refreshToken });

        const { accessToken: newToken, refreshToken: newRefresh, accessTokenExpiresAt } = response.data;
        setTokens({ accessToken: newToken, refreshToken: newRefresh, accessTokenExpiresAt });
        onTokenRefreshed(newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return httpClient(originalRequest);
      } catch {
        // Notify waiting subscribers that refresh failed
        onTokenRefreshed('');
        refreshSubscribers = [];
        logout();
        window.location.href = '/login';
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

// Proactive token refresh (before expiry)
export function setupTokenRefreshTimer(): () => void {
  const intervalId = setInterval(() => {
    const { accessTokenExpiresAt, refreshToken } = useAuthStore.getState();
    if (accessTokenExpiresAt === null || refreshToken === null) return;

    const now = Date.now();
    const expiresAt = accessTokenExpiresAt * 1000; // convert to ms
    const timeUntilExpiry = expiresAt - now;

    if (timeUntilExpiry > 0 && timeUntilExpiry < TOKEN_REFRESH_THRESHOLD) {
      void httpClient.post('/auth/refresh', { refreshToken }).then((response) => {
        const data = response.data as {
          accessToken: string;
          refreshToken: string;
          accessTokenExpiresAt: number;
        };
        useAuthStore.getState().setTokens({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          accessTokenExpiresAt: data.accessTokenExpiresAt,
        });
      }).catch(() => {
        // Refresh failed, force logout
        useAuthStore.getState().logout();
        window.location.href = '/login';
      });
    }
  }, 30000);
  return () => clearInterval(intervalId);
}

export default httpClient;
